import { useEffect, useRef } from 'react';
import { ArrowRight, Users, Wrench, Zap } from 'lucide-react';
import { motion, useInView, useAnimation } from 'framer-motion';
import VideoComponent from './Video';
import LogoCarousel from './LogoCarousel';

const statsData = [
  {
    number: '+ de 50.000',
    label: 'Advogados confiam',
    icon: Users,
    delay: 0.2,
  },
  {
    number: '+ de 35',
    label: 'Ferramentas',
    icon: Wrench,
    delay: 0.4,
  },
  {
    number: '11x',
    label: 'Mais produtividade',
    icon: Zap,
    delay: 0.6,
  },
];

export default function Hero2() {
  const statsRef = useRef(null);
  const isInView = useInView(statsRef, { once: true, margin: '-100px' });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  useEffect(() => {
    const urlParams = window.location.search;
    const button = document.getElementById('ctaButton') as HTMLAnchorElement;

    if (urlParams && button) {
      const cleanParams = urlParams.startsWith('?') ? urlParams.substring(1) : urlParams;
      const buttonUrl = new URL(button.href);
      buttonUrl.search = cleanParams;
      button.href = buttonUrl.toString();
    }
  }, []);

  return (
    <div className="relative min-h-screen flex items-center pt-12" style={{ overflow: 'visible' }}>
      {/* Background gradient effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#28E7C5] opacity-20 blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-[#6942E2] opacity-20 blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative container mx-auto px-6 py-16 md:py-16" style={{ overflow: 'visible' }}>
        <div className="max-w-4xl mx-auto text-center" style={{ overflow: 'visible' }}>
          <h1
            className="text-4xl md:text-6xl font-bold mb-8 
                       bg-gradient-to-r from-[#28E7C5] to-[#6942E2] 
                       bg-clip-text text-transparent inline-block 
                       leading-[1.2] py-2"
            style={{
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              overflow: 'visible',
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            }}
          >
            Inteligência Artificial para Advogados
          </h1>

          <p className="text-xl md:text-2xl mb-12 text-gray-300">
            Aumente sua produtividade e qualidade com uma IA treinada especificamente para o Direito brasileiro
          </p>

          <div className="relative w-full aspect-video mb-12 rounded-xl overflow-hidden backdrop-blur-lg bg-white/5 border border-white/10">
            <div className="absolute inset-0 flex items-center justify-center">
              <VideoComponent />
            </div>
          </div>

          {/* CTA Button with shining effect */}
          <a 
              id="ctaButton"
              href="https://plataforma.lawx.ai/dashboard" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group"
            >
              <button
                className="group relative px-8 py-4 
                           bg-gradient-to-r from-[#28E7C5] to-[#6942E2] 
                           text-white rounded-lg font-semibold text-lg 
                           transition-all duration-300 
                           hover:shadow-[0_0_20px_rgba(40,231,197,0.4)] 
                           hover:scale-105 mx-auto overflow-hidden"
              >
                Comece Agora
                <ArrowRight className="inline-block ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent 
                             opacity-0 group-hover:opacity-100 
                             transform -translate-x-full skew-x-12 
                             group-hover:translate-x-full 
                             transition-transform duration-700 ease-in-out pointer-events-none"
                />
              </button>
            </a>
            <LogoCarousel />

          {/* Stats with glass effect */}
          <div ref={statsRef} className="mt-8 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {statsData.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={controls}
                  variants={{
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        delay: stat.delay,
                        ease: 'easeOut',
                      },
                    },
                  }}
                  transition={{
                    duration: 0.5,
                    delay: stat.delay,
                    ease: 'easeOut',
                  }}
                  className="group backdrop-blur-lg bg-white/10 rounded-lg p-6 border border-white/20 
                             flex flex-col items-center relative 
                             transition-all duration-500 ease-out
                             hover:transform hover:scale-105 hover:-translate-y-2
                             hover:bg-white/15 hover:border-[#28E7C5]/40
                             hover:shadow-[0_0_30px_rgba(40,231,197,0.2)]
                             before:absolute before:inset-0 
                             before:bg-gradient-to-r before:from-transparent before:via-white/5 before:to-transparent
                             before:opacity-0 before:transition-opacity before:duration-500
                             hover:before:opacity-100"
                >
                  <Icon className="w-8 h-8 mb-4 text-[#28E7C5] transition-transform duration-500 group-hover:scale-110 group-hover:text-[#6942E2]" />
                  <div className="text-3xl font-bold mb-2 transition-colors duration-500 group-hover:text-white">
                    {stat.number}
                  </div>
                  <div className="text-gray-300 transition-colors duration-500 group-hover:text-[#28E7C5]">
                    {stat.label}
                  </div>
                  <div className="absolute -inset-px bg-gradient-to-r from-[#28E7C5]/0 via-[#28E7C5]/10 to-[#28E7C5]/0 
                                opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
} 